import '../plugins/zepto';
import Promise from 'promise';
import PinchZoom from '../plugins/pinchzoom';
import valueInRange from '../utils/valueInRange';
import debounce from 'lodash.debounce';

var Map = {
  animate: false,
  disableEvents: false,
  hasRendered: false,
  isNarrow: window.innerWidth < 768,
  minHeight: 320,
  minZoom: 1,
  maxZoom: 2,
  zoomWidthMin: 300,
  zoomWidthMax: 1400,

  ui: {
    map: '#map',
    loading: '#map-loading',
    zoomIn: '.map-zoom__btn.-in',
    zoomOut: '.map-zoom__btn.-out',
  },

  events: {
    'click {zoomIn}': 'zoomIn',
    'click {zoomOut}': 'zoomOut',
  },

  initialize: function () {
    this.configure();
    this.minZoom =
      parseInt(this.el.getAttribute('data-min-zoom'), 10) || this.minZoom;
    this.maxZoom =
      parseInt(this.el.getAttribute('data-max-zoom'), 10) || this.maxZoom;
    this.render().then(this.position.bind(this));
  },

  appendMarkerIcon: function () {
    if (this.markerIcon) {
      return;
    }
    var marker = document.createElement('div');
    marker.setAttribute('id', 'map-marker');
    marker.innerHTML = `
      <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" height="48" width="48">
        <ellipse opacity=".2" cx="24" cy="46" rx="12" ry="2"/>
        <path fill="#f75404" d="M24 0c-8.8 0-15.8 7.1-16 16.1-.1 6.6 2.5 9.6 5.6 13.2 2.8 3.2 5.9 6.8 8.5 14.3.3.8 1 1.3 1.9 1.3s1.6-.5 1.9-1.3c2.7-7.5 5.8-11.1 8.5-14.3 3.1-3.6 5.7-6.6 5.6-13.2-.2-9-7.2-16.1-16-16.1zm0 24c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z"/>
      </svg>
    `;
    this.markerIcon = this.ui.map[0].appendChild(marker);
  },

  canZoomIn: function () {
    return this.pz.zoomFactor < this.zoomLevelMax();
  },

  canZoomOut: function () {
    return this.pz.zoomFactor > 1;
  },

  configure: function () {
    this.minZoom =
      parseInt(this.el.getAttribute('data-min-zoom'), 10) || this.minZoom;
    this.maxZoom =
      parseInt(this.el.getAttribute('data-max-zoom'), 10) || this.maxZoom;

    var hasTouch =
      'ontouchstart' in window ||
      (window.DocumentTouch && document instanceof DocumentTouch);
    document.documentElement.classList.add(hasTouch ? 'touch' : 'no-touch');
  },

  drag: function (position) {
    var pzDrag = this.animate ? 'animateDrag' : 'drag';
    this.pz[pzDrag](position, this.pz.lastDragPosition || this.pz.offset);
    this.pz.lastDragPosition = position;
    this.pz.end();
  },

  getMarker: function () {
    return this.defaultMarker;
  },

  getScale: function () {
    return this.pz.getInitialZoomFactor() * this.pz.zoomFactor;
  },

  loadImage: function () {
    return new Promise((resolve, reject) => {
      var img = new Image();
      img.onload = function (e) {
        resolve(e.target);
      };
      img.alt = '';
      img.src = this.ui.map[0].getAttribute('data-image');
    });
  },

  onZoomStart: function () {
    this.setCursor('default');
  },

  onZoomEnd: function () {
    this.setZoomUI();
  },

  onDragStart: function () {
    if (this.pz.zoomFactor > 1) {
      this.setCursor('move');
    }
  },

  onDragEnd: function () {
    this.setZoomUI();
  },

  placeMarkerIcon: function (marker) {
    var left = marker.map_x || 50;
    var top = marker.map_y || 50;
    this.appendMarkerIcon();
    this.markerIcon.setAttribute(
      'style',
      'left:' + left + '%; top:' + top + '%;',
    );
  },

  position: function (slug, zoom) {
    zoom = zoom || this.zoomLevel();
    var marker = this.getMarker(slug);
    var x = (marker.map_x / 100) * this.width;
    var y = (marker.map_y / 100) * this.height;

    setTimeout(() => {
      // defer render() completion
      this.scale(zoom);
      this.drag({
        x: -(x * this.getScale() - this.el.offsetWidth / 2),
        y: -(y * this.getScale() - this.el.offsetHeight / 2),
      });
      this.placeMarkerIcon(marker);
    }, 0);
  },

  render: function (img) {
    if (this.hasRendered) {
      return Promise.resolve();
    }
    return this.loadImage().then((img) => {
      var loading = this.ui.loading[0];
      loading && loading.parentNode.removeChild(loading);
      this.ui.map[0].appendChild(img);
      this.setDimensions(img);

      this.pz = new PinchZoom(this.ui.map[0], {
        disableEvents: this.disableEvents,
        initZoom: this.zoomLevel(),
        lockBoundaries: this.lockBoundaries,
        minZoom: 1,
        maxZoom: this.zoomLevelMax(),
        zoomOutFactor: 1,
      });

      if (!this.disableEvents) {
        this.pz.el.on({
          pz_zoomstart: debounce(this.onZoomStart.bind(this), 250, true),
          pz_zoomend: debounce(this.onZoomEnd.bind(this), 100),
          pz_dragstart: this.onDragStart.bind(this),
          pz_dragend: this.onDragEnd.bind(this),
        });

        this.setZoomButtons();
        this.setCursor('zoom-in');
      }

      this.hasRendered = true;
    });
  },

  scale: function (zoom, offset) {
    var offset = offset || this.pz.offset;
    this.pz.scaleTo(zoom, offset);
  },

  scaleCenter: function (zoom) {
    this.scale(zoom, {
      x: this.el.offsetWidth / 2,
      y: this.el.offsetHeight / 2,
    });
    this.pz.end();
    this.onZoomEnd();
  },

  setCursor: function (cursor) {
    this.ui.map[0].style.cursor = cursor;
    this.ui.map[0].style['-webkit-cursor'] = `-webkit-${cursor}`;
  },

  setZoomButtons: function () {
    this.ui.zoomIn[0].classList[this.canZoomIn() ? 'add' : 'remove']('active');
    this.ui.zoomOut[0].classList[this.canZoomOut() ? 'add' : 'remove'](
      'active',
    );
  },

  setZoomCursor: function () {
    var cursor = this.canZoomOut() ? 'zoom-out' : 'zoom-in';
    this.setCursor(cursor);
  },

  setZoomUI: function () {
    this.setZoomButtons();
    this.setZoomCursor();
  },

  setDimensions: function (img) {
    this.height = img.naturalHeight;
    this.width = img.naturalWidth;

    this.setMapHeight(Math.max(window.innerHeight * 0.8, this.minHeight));
  },

  setMapHeight: function (height) {
    if (this.mapHeight) {
      this.el.setAttribute('style', `height: ${this.mapHeight}px`);
    }
  },

  zoomIn: function () {
    if (this.canZoomIn()) {
      this.scaleCenter(this.pz.zoomFactor + 0.3);
    }
  },

  zoomLevel: function () {
    var range = this.zoomRange();
    return this.isNarrow ? range * 2 : range;
  },

  zoomLevelMax: function () {
    return this.isNarrow ? this.maxZoom * 2 : this.maxZoom;
  },

  zoomOut: function () {
    if (this.canZoomOut()) {
      this.scaleCenter(this.pz.zoomFactor - 0.3);
    }
  },

  zoomRange: function () {
    return valueInRange(this.minZoom, this.maxZoom, this.zoomWidthRatio());
  },

  zoomWidthRatio: function () {
    var min = this.zoomWidthMin;
    var max = this.zoomWidthMax;
    var width = Math.max(Math.min(this.el.offsetWidth, max) - min, 0);
    return 1 - width / (max - min);
  },
};

export default Map;
